import React, {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
} from "react";
import { Button, ButtonGroup } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { icons } from "../../../svg";
import { competitorMenuItems, kochMenuItems } from "../../../utils/constants";
import { formatRequestedAtDateTime } from "../../../utils/utils";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useNitroStore } from "../../../store/Store";
import TruckIconFO from "../../../icons/TruckIconFO";
import RailIconFO from "../../../icons/RailIconFO";
import {
  ActionType,
  FavoritePriceShowAs,
  IFavoriteOriginMonth,
  IFavoriteOriginPeriod,
  IFavoriteOriginPeriodPrice,
  OriginDisplayInfo,
  PriceField,
} from "../../../types/IFavoriteOrigins";

interface OriginRowProps {
  origin: OriginDisplayInfo;
  outboundModes?: string[];
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: PriceField, value: string) => void;
  activeMenuId: string | null;
  setActiveMenuId: (id: string | null) => void;
  onAction: (type: ActionType, origin: OriginDisplayInfo) => void;
  type: string;
  onShowTapHere?: (show: boolean) => void;
  selectedMonth?: IFavoriteOriginMonth;
  selectedMonthKey?: string;
  onMonthChange?: (month: IFavoriteOriginMonth) => void;
}

export interface SplitOriginRowHandle {
  flush: () => void;
}

type PriceMode = "Price" | "Discuss" | "No Offer";
type FavoriteDate = IFavoriteOriginPeriod["startDate"];

interface MonthPayloadOverrides {
  postedPxMode?: PriceMode;
  postedPxValue?: string;
  truckMode?: PriceMode;
  truckValue?: string;
  railMode?: PriceMode;
  railValue?: string;
  postedPxMode2?: PriceMode;
  postedPxValue2?: string;
  truckMode2?: PriceMode;
  truckValue2?: string;
  railMode2?: PriceMode;
  railValue2?: string;
}

const emptyDate = (): FavoriteDate => ({ year: 0, month: 0, day: 0 });

const toDateObj = (value: unknown): FavoriteDate => {
  if (!value) return emptyDate();

  if (typeof value === "string") {
    const [year, month, day] = value.split("-").map(Number);
    return {
      year: Number.isFinite(year) ? year : 0,
      month: Number.isFinite(month) ? month : 0,
      day: Number.isFinite(day) ? day : 0,
    };
  }

  if (typeof value === "object") {
    const raw = value as Partial<FavoriteDate>;
    return {
      year: Number(raw.year) || 0,
      month: Number(raw.month) || 0,
      day: Number(raw.day) || 0,
    };
  }

  return emptyDate();
};

const toMonthObj = (value: unknown): FavoriteDate => {
  const parsed = toDateObj(value);
  return {
    ...parsed,
    day: parsed.day || 1,
  };
};

const modeFromShowAs = (showAs?: FavoritePriceShowAs): PriceMode => {
  if (showAs === "DISCUSS") return "Discuss";
  if (showAs === "NO_OFFER") return "No Offer";
  return "Price";
};

const showAsFromMode = (mode: PriceMode): FavoritePriceShowAs => {
  if (mode === "Discuss") return "DISCUSS";
  if (mode === "No Offer") return "NO_OFFER";
  return "PRICE";
};

const valueFromPeriodPrice = (value?: IFavoriteOriginPeriodPrice | null) => {
  if (!value || value.price === null || value.price === undefined) return "";
  return String(value.price);
};

const createEmptyPeriodPrice = (): IFavoriteOriginPeriodPrice => ({
  price: null,
  showAs: "PRICE",
  requestedAt: "",
});

const toPeriodPrice = (
  mode: PriceMode,
  value: string,
  base?: IFavoriteOriginPeriodPrice | null,
): IFavoriteOriginPeriodPrice => {
  if (mode !== "Price" || value.trim() === "") {
    return {
      showAs: showAsFromMode(mode),
      price: null,
      requestedBy: base?.requestedBy ?? "",
      requestedAt: base?.requestedAt || "",
    };
  }

  const parsed = Number(value);
  return {
    showAs: showAsFromMode(mode),
    price: Number.isFinite(parsed) ? parsed : null,
    requestedBy: base?.requestedBy ?? "",
    requestedAt: base?.requestedAt || "",
  };
};

const buildPeriod = (
  base: IFavoriteOriginPeriod | undefined,
  postedMode: PriceMode,
  postedValue: string,
  truckModeValue: PriceMode,
  truckValue: string,
  railModeValue: PriceMode,
  railValue: string,
): IFavoriteOriginPeriod => ({
  startDate: toDateObj(base?.startDate),
  endDate: toDateObj(base?.endDate),
  prices: {
    posted: toPeriodPrice(postedMode, postedValue, base?.prices?.posted),
    truck: toPeriodPrice(truckModeValue, truckValue, base?.prices?.truck),
    rail: toPeriodPrice(railModeValue, railValue, base?.prices?.rail),
  },
});

const toRequestedAtDate = (requestedAt: unknown): Date | null => {
  if (!requestedAt) return null;

  if (typeof requestedAt === "string") {
    const parsed = new Date(requestedAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof requestedAt === "object") {
    const raw = requestedAt as {
      year?: number;
      month?: number;
      day?: number;
      hour?: number;
      minute?: number;
      second?: number;
    };
    const year = Number(raw.year) || 0;
    const month = Number(raw.month) || 0;
    const day = Number(raw.day) || 0;

    if (!year || !month || !day) return null;

    return new Date(
      year,
      month - 1,
      day,
      Number(raw.hour) || 0,
      Number(raw.minute) || 0,
      Number(raw.second) || 0,
    );
  }

  return null;
};

const periodPriceEntries: {
  label: string;
  mode: "posted" | "truck" | "rail";
}[] = [
  { label: "Posted Px", mode: "posted" },
  { label: "Truck ALT", mode: "truck" },
  { label: "Rail ALT", mode: "rail" },
];

const getPeriodUpdateInfo = (period?: IFavoriteOriginPeriod) => {
  const rows = periodPriceEntries.map(({ label, mode }) => ({
    label,
    value: period?.prices?.[mode]?.requestedAt || "-",
  }));

  const latest = periodPriceEntries.reduce<{
    priceItem: IFavoriteOriginPeriodPrice;
    requestedAtMs: number;
  } | null>((best, { mode }) => {
    const priceItem = period?.prices?.[mode];
    if (!priceItem) return best;

    const requestedDate = toRequestedAtDate(priceItem.requestedAt);
    if (!requestedDate) return best;

    const requestedAtMs = requestedDate.getTime();
    if (!best || requestedAtMs > best.requestedAtMs) {
      return { priceItem, requestedAtMs };
    }

    return best;
  }, null);

  return {
    requestedBy: latest?.priceItem.requestedBy || "-",
    rows,
  };
};

const SplitOriginRow = React.forwardRef<SplitOriginRowHandle, OriginRowProps>(
  (
    {
      origin,
      outboundModes,
      onRemove,
      onUpdate,
      activeMenuId,
      setActiveMenuId,
      onAction,
      type,
      onShowTapHere,
      selectedMonth,
      selectedMonthKey,
      onMonthChange,
    },
    ref,
  ) => {
    const initMenuItems = type === "koch" ? kochMenuItems : competitorMenuItems;
    const [showCompetitiveAnalysisModal] = useNitroStore((state) => [
      state.showCompetitiveAnalysisModal,
    ]);
    const isMobile = useIsMobile(768);

    let menuItems = initMenuItems;
    if (isMobile && showCompetitiveAnalysisModal) {
      menuItems = menuItems.filter((item) => item.action !== "overview");
    }
    if (isMobile) {
      menuItems = menuItems.filter((item) => item.action !== "remove");
    }

    const [prices, setPrices] = useState({
      truckMin: origin.truckMin,
      truckMax: origin.truckMax,
      railMin: origin.railMin,
      railMax: origin.railMax,
    });

    const [isTruckSelected, setIsTruckSelected] = useState(
      origin.truckEnabled === "true",
    );
    const [isRailSelected, setIsRailSelected] = useState(
      origin.railEnabled === "true",
    );

    const [isSplit, setIsSplit] = useState(false);
    const [postedPxValue, setPostedPxValue] = useState("");
    const [postedPxMode, setPostedPxMode] = useState<PriceMode>("Price");
    const [truckMode, setTruckMode] = useState<PriceMode>("Price");
    const [railMode, setRailMode] = useState<PriceMode>("Price");
    const [postedPxMode2, setPostedPxMode2] = useState<PriceMode>("Price");
    const [truckMode2, setTruckMode2] = useState<PriceMode>("Price");
    const [railMode2, setRailMode2] = useState<PriceMode>("Price");
    const [splitSecondPrices, setSplitSecondPrices] = useState({
      postedPx: "",
      truckAlt: "",
      railAlt: "",
    });
    const [lastUpdatedAnchorEl, setLastUpdatedAnchorEl] =
      useState<HTMLElement | null>(null);
    const lastUpdatedWrapRef = React.useRef<HTMLDivElement | null>(null);
    const selectPrice = (event: React.SyntheticEvent<HTMLInputElement>) => {
      if (!event.currentTarget.readOnly) event.currentTarget.select();
    };

    const normalizedOutboundModes = React.useMemo(
      () =>
        (outboundModes || []).map((mode) => String(mode || "").toUpperCase()),
      [outboundModes],
    );
    const showTruckPriceRow = normalizedOutboundModes.includes("TRUCK");
    const showRailPriceRow = normalizedOutboundModes.includes("RAIL");
    const showPriceSection =
      type === "koch" || showTruckPriceRow || showRailPriceRow;

    const buildMonthPayload = React.useCallback(
      (overrides?: MonthPayloadOverrides): IFavoriteOriginMonth => {
        const firstPeriodBase =
          selectedMonth?.periods?.[0] ??
          (() => {
            const monthObj = toMonthObj(selectedMonthKey);
            if (!monthObj.year || !monthObj.month) return undefined;
            const { year, month } = monthObj;
            const lastDay = new Date(year, month, 0).getDate();
            return {
              startDate: { year, month, day: 1 },
              endDate: { year, month, day: lastDay },
              prices: {
                posted: createEmptyPeriodPrice(),
                truck: createEmptyPeriodPrice(),
                rail: createEmptyPeriodPrice(),
              },
            };
          })();
        const secondPeriodBase =
          selectedMonth?.periods?.[1] ??
          (() => {
            const monthObj = toMonthObj(
              selectedMonth?.month || selectedMonthKey,
            );
            if (!monthObj.year || !monthObj.month) return undefined;
            const { year, month } = monthObj;
            const lastDay = new Date(year, month, 0).getDate();
            return {
              startDate: { year, month, day: 16 },
              endDate: { year, month, day: lastDay },
              prices: {
                posted: createEmptyPeriodPrice(),
                truck: createEmptyPeriodPrice(),
                rail: createEmptyPeriodPrice(),
              },
            };
          })();

        const firstTruckValue =
          type === "koch" ? prices.truckMin : prices.truckMax;
        const firstRailValue =
          type === "koch" ? prices.railMin : prices.railMax;

        const firstPeriod = buildPeriod(
          firstPeriodBase,
          overrides?.postedPxMode ?? postedPxMode,
          overrides?.postedPxValue ?? postedPxValue,
          overrides?.truckMode ?? truckMode,
          overrides?.truckValue ?? firstTruckValue,
          overrides?.railMode ?? railMode,
          overrides?.railValue ?? firstRailValue,
        );

        if (!isSplit) {
          return {
            month: toMonthObj(selectedMonth?.month || selectedMonthKey),
            isSplit: false,
            periods: [firstPeriod],
          };
        }

        return {
          month: toMonthObj(selectedMonth?.month || selectedMonthKey),
          isSplit: true,
          periods: [
            firstPeriod,
            buildPeriod(
              secondPeriodBase,
              overrides?.postedPxMode2 ?? postedPxMode2,
              overrides?.postedPxValue2 ?? splitSecondPrices.postedPx,
              overrides?.truckMode2 ?? truckMode2,
              overrides?.truckValue2 ?? splitSecondPrices.truckAlt,
              overrides?.railMode2 ?? railMode2,
              overrides?.railValue2 ?? splitSecondPrices.railAlt,
            ),
          ],
        };
      },
      [
        isSplit,
        postedPxMode,
        postedPxValue,
        truckMode,
        railMode,
        postedPxMode2,
        splitSecondPrices.postedPx,
        splitSecondPrices.truckAlt,
        splitSecondPrices.railAlt,
        truckMode2,
        railMode2,
        selectedMonth,
        prices.truckMin,
        prices.truckMax,
        prices.railMin,
        prices.railMax,
        selectedMonthKey,
        type,
      ],
    );

    useEffect(() => {
      setPrices({
        truckMin: origin.truckMin,
        truckMax: origin.truckMax,
        railMin: origin.railMin,
        railMax: origin.railMax,
      });
    }, [origin.truckMin, origin.truckMax, origin.railMin, origin.railMax]);

    useEffect(() => {
      const firstPeriod = selectedMonth?.periods?.[0];
      const secondPeriod = selectedMonth?.periods?.[1];

      const nextTruckFirst = valueFromPeriodPrice(firstPeriod?.prices?.truck);
      const nextRailFirst = valueFromPeriodPrice(firstPeriod?.prices?.rail);

      setIsSplit(
        typeof selectedMonth?.isSplit === "boolean"
          ? selectedMonth.isSplit
          : (selectedMonth?.periods?.length || 0) > 1,
      );
      setPostedPxValue(valueFromPeriodPrice(firstPeriod?.prices?.posted));
      setPostedPxMode(modeFromShowAs(firstPeriod?.prices?.posted?.showAs));
      setTruckMode(modeFromShowAs(firstPeriod?.prices?.truck?.showAs));
      setRailMode(modeFromShowAs(firstPeriod?.prices?.rail?.showAs));

      setPostedPxMode2(modeFromShowAs(secondPeriod?.prices?.posted?.showAs));
      setTruckMode2(modeFromShowAs(secondPeriod?.prices?.truck?.showAs));
      setRailMode2(modeFromShowAs(secondPeriod?.prices?.rail?.showAs));

      setPrices({
        truckMin: type === "koch" ? nextTruckFirst : origin.truckMin,
        truckMax: type === "koch" ? origin.truckMax : nextTruckFirst,
        railMin: type === "koch" ? nextRailFirst : origin.railMin,
        railMax: type === "koch" ? origin.railMax : nextRailFirst,
      });

      setSplitSecondPrices({
        postedPx: valueFromPeriodPrice(secondPeriod?.prices?.posted),
        truckAlt: valueFromPeriodPrice(secondPeriod?.prices?.truck),
        railAlt: valueFromPeriodPrice(secondPeriod?.prices?.rail),
      });
    }, [
      selectedMonth,
      type,
      origin.truckMin,
      origin.truckMax,
      origin.railMin,
      origin.railMax,
    ]);

    // Builds the payload and notifies the parent, skipping the call if nothing actually changed.
    const commitMonthChange = React.useCallback(
      (overrides?: MonthPayloadOverrides) => {
        if (!onMonthChange) return;

        const nextPayload = buildMonthPayload(overrides);

        if (
          selectedMonth &&
          JSON.stringify(nextPayload) === JSON.stringify(selectedMonth)
        ) {
          return;
        }

        onMonthChange(nextPayload);
      },
      [onMonthChange, selectedMonth, buildMonthPayload],
    );

    useImperativeHandle(ref, () => ({ flush: () => commitMonthChange() }), [
      commitMonthChange,
    ]);

    const handleSplitToggle = () => {
      const newIsSplit = !isSplit;
      const monthObj = toMonthObj(selectedMonth?.month || selectedMonthKey);

      if (onMonthChange && monthObj.year > 0 && monthObj.month > 0) {
        const { year, month } = monthObj;

        let newMonthPayload: IFavoriteOriginMonth;

        if (newIsSplit) {
          const currentPeriod = selectedMonth?.periods?.[0];
          const lastDay = new Date(year, month, 0).getDate(); // compute the last day of the selected month

          const period1: IFavoriteOriginPeriod = {
            startDate: currentPeriod?.startDate || { year, month, day: 1 },
            endDate: { year, month, day: 15 },
            prices: currentPeriod?.prices || {
              posted: createEmptyPeriodPrice(),
              truck: createEmptyPeriodPrice(),
              rail: createEmptyPeriodPrice(),
            },
          };

          const period2: IFavoriteOriginPeriod = {
            startDate: { year, month, day: 16 },
            endDate: { year, month, day: lastDay },
            prices: {
              posted: createEmptyPeriodPrice(),
              truck: createEmptyPeriodPrice(),
              rail: createEmptyPeriodPrice(),
            },
          };

          newMonthPayload = {
            month: monthObj,
            isSplit: true,
            periods: [period1, period2],
          };

          setSplitSecondPrices({ postedPx: "", truckAlt: "", railAlt: "" });
        } else {
          const firstHalf =
            selectedMonth?.periods?.find((p) => p.endDate?.day === 15) ||
            selectedMonth?.periods?.[0] ||
            null;

          newMonthPayload = {
            month: monthObj,
            isSplit: false,
            periods: firstHalf
              ? [{ ...firstHalf }]
              : [
                  {
                    startDate: { year, month, day: 1 },
                    endDate: {
                      year,
                      month,
                      day: new Date(year, month, 0).getDate(),
                    },
                    prices: {
                      posted: createEmptyPeriodPrice(),
                      truck: createEmptyPeriodPrice(),
                      rail: createEmptyPeriodPrice(),
                    },
                  },
                ],
          };
        }

        onMonthChange(newMonthPayload);
      }

      setIsSplit(newIsSplit);
    };

    useEffect(() => {
      setIsTruckSelected(origin.truckEnabled === "true");
      setIsRailSelected(origin.railEnabled === "true");
    }, [origin.truckEnabled, origin.railEnabled]);

    const handleValueChange = (field: PriceField, value: string) => {
      const newPrices = { ...prices, [field]: value };
      setPrices(newPrices);
      onUpdate(origin.id, field, value);

      if ((field === "truckMax" || field === "railMax") && onShowTapHere) {
        const truckMaxValue = field === "truckMax" ? value : newPrices.truckMax;
        const railMaxValue = field === "railMax" ? value : newPrices.railMax;

        const bothEmpty =
          (!truckMaxValue || truckMaxValue.trim() === "") &&
          (!railMaxValue || railMaxValue.trim() === "");

        onShowTapHere(bothEmpty);
      }
    };

    const isMenuOpen = activeMenuId === origin.id;
    const handleMenuToggle = () =>
      setActiveMenuId(isMenuOpen ? null : origin.id);
    const handleActionClick = (action: any) => {
      onAction(action, origin);
      setActiveMenuId(null);
    };

    const handleRemove = () => {
      if (isTruckSelected) {
        handleValueChange("truckEnabled", "false");
      }
      if (isRailSelected) {
        handleValueChange("railEnabled", "false");
      }
      onRemove(origin.id);
    };

    const dateLabel = useMemo(() => {
      const monthObj = toMonthObj(selectedMonth?.month || selectedMonthKey);
      const hasValidMonth = monthObj.year > 0 && monthObj.month > 0;
      const sourceDate = hasValidMonth
        ? new Date(monthObj.year, monthObj.month - 1, 1)
        : new Date();

      return `${sourceDate.toLocaleDateString("en-US", {
        month: "short",
      })} ${sourceDate.toLocaleDateString("en-US", {
        year: "2-digit",
      })}'`;
    }, [selectedMonth, selectedMonthKey]);

    const isLastUpdatedPopupOpen = Boolean(lastUpdatedAnchorEl);

    // one entry per period so split months show 1st/2nd half updates separately
    const periodUpdateSections = useMemo(() => {
      const periods = selectedMonth?.periods || [];
      const showHalfLabels = periods.length > 1;

      return periods.map((period, index) => ({
        // eslint-disable-next-line no-nested-ternary
        half: showHalfLabels ? (index === 0 ? "1ST HALF" : "2ND HALF") : null,
        ...getPeriodUpdateInfo(period),
      }));
    }, [selectedMonth]);

    const handleLastUpdatedInfoClick = (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      const target = event.currentTarget as HTMLElement;

      setLastUpdatedAnchorEl((prev) => {
        return prev ? null : target;
      });
    };

    useEffect(() => {
      if (!isLastUpdatedPopupOpen) return undefined;

      const handleOutsideClick = (event: MouseEvent) => {
        if (
          lastUpdatedWrapRef.current &&
          !lastUpdatedWrapRef.current.contains(event.target as Node)
        ) {
          setLastUpdatedAnchorEl(null);
        }
      };

      document.addEventListener("mousedown", handleOutsideClick);

      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }, [isLastUpdatedPopupOpen]);

    const renderPriceRows = (half: "main" | "first" | "second") => {
      const isSecond = half === "second";
      const isKoch = type === "koch";

      const postedValue = isSecond ? splitSecondPrices.postedPx : postedPxValue;
      let truckAltValue = isSecond
        ? splitSecondPrices.truckAlt
        : prices.truckMax;
      let railAltValue = isSecond ? splitSecondPrices.railAlt : prices.railMax;

      if (!isSecond && isKoch) {
        truckAltValue = prices.truckMin;
        railAltValue = prices.railMin;
      }

      const curPostedPxMode = isSecond ? postedPxMode2 : postedPxMode;
      const curTruckMode = isSecond ? truckMode2 : truckMode;
      const curRailMode = isSecond ? railMode2 : railMode;

      const onPostedPxModeChange = (mode: PriceMode) => {
        if (mode !== "Price") {
          onPostedPxChange("");
        }
        if (isSecond) setPostedPxMode2(mode);
        else setPostedPxMode(mode);

        // Discuss/No Offer have no editable value, so persist as soon as the mode is picked.
        if (mode !== "Price") {
          commitMonthChange(
            isSecond
              ? { postedPxMode2: mode, postedPxValue2: "" }
              : { postedPxMode: mode, postedPxValue: "" },
          );
        }
      };
      const onTruckModeChange = (mode: PriceMode) => {
        if (mode !== "Price") {
          onTruckAltChange("");
        }
        if (isSecond) setTruckMode2(mode);
        else setTruckMode(mode);

        if (mode !== "Price") {
          commitMonthChange(
            isSecond
              ? { truckMode2: mode, truckValue2: "" }
              : { truckMode: mode, truckValue: "" },
          );
        }
      };
      const onRailModeChange = (mode: PriceMode) => {
        if (mode !== "Price") {
          onRailAltChange("");
        }
        if (isSecond) setRailMode2(mode);
        else setRailMode(mode);

        if (mode !== "Price") {
          commitMonthChange(
            isSecond
              ? { railMode2: mode, railValue2: "" }
              : { railMode: mode, railValue: "" },
          );
        }
      };

      const onPostedPxChange = (val: string) => {
        if (isSecond) {
          setSplitSecondPrices((prev) => ({ ...prev, postedPx: val }));
        } else {
          setPostedPxValue(val);
          if (onShowTapHere) {
            onShowTapHere(
              (!val || val.trim() === "") &&
                (!prices.truckMin || prices.truckMin.trim() === "") &&
                (!prices.railMin || prices.railMin.trim() === ""),
            );
          }
        }
      };

      const onTruckAltChange = (val: string) => {
        if (isSecond) {
          setSplitSecondPrices((prev) => ({ ...prev, truckAlt: val }));
        } else {
          setPrices((prev) => ({
            ...prev,
            [isKoch ? "truckMin" : "truckMax"]: val,
          }));
        }
      };

      const onRailAltChange = (val: string) => {
        if (isSecond) {
          setSplitSecondPrices((prev) => ({ ...prev, railAlt: val }));
        } else {
          setPrices((prev) => ({
            ...prev,
            [isKoch ? "railMin" : "railMax"]: val,
          }));
        }
      };

      const getDisplayValue = (mode: PriceMode, value: string) => {
        if (mode === "Discuss") return "DISC";
        if (mode === "No Offer") return "N/O";
        if (mode === "Price" && value && value.trim() !== "") {
          return value.startsWith("$") ? value : `$${value}`;
        }
        return "";
      };

      const renderModeButtons = (
        mode: PriceMode,
        onChange: (m: PriceMode) => void,
      ) => (
        <ButtonGroup
          id="price-mode-btn-group"
          size="small"
          className="price-mode-btn-group"
        >
          <Button
            id="price-mode-price-id"
            className={`mode-btn${
              mode === "Price" ? " mode-btn-selected" : ""
            }`}
            onClick={() => onChange("Price")}
          >
            Price
          </Button>
          {isKoch && (
            <Button
              id="price-mode-discuss-id"
              className={`mode-btn${
                mode === "Discuss" ? " mode-btn-selected" : ""
              }`}
              onClick={() => onChange("Discuss")}
            >
              Discuss
            </Button>
          )}
          <Button
            id="price-mode-no-offer-id"
            className={`mode-btn${
              mode === "No Offer" ? " mode-btn-selected" : ""
            }`}
            onClick={() => onChange("No Offer")}
          >
            No Offer
          </Button>
        </ButtonGroup>
      );

      return (
        <div className="price-rows-container">
          {isKoch && (
            <div className="price-row">
              <span className="price-row-label posted-px-label">Posted PX</span>
              {renderModeButtons(curPostedPxMode, onPostedPxModeChange)}
              <input
                id="posted-price-input"
                type="text"
                inputMode="numeric"
                placeholder="Price"
                className={`price-display-input ${
                  curPostedPxMode !== "Price" ? "is-italic" : ""
                } 
              ${
                !getDisplayValue(curPostedPxMode, postedValue)
                  ? "is-default-placeholder"
                  : ""
              }`}
                value={getDisplayValue(curPostedPxMode, postedValue)}
                onFocus={selectPrice}
                onClick={selectPrice}
                onContextMenu={(event) => event.preventDefault()}
                onChange={(e) => {
                  if (curPostedPxMode === "Price") {
                    const cleanValue = e.target.value.replace(/[^0-9.]/g, "");
                    onPostedPxChange(cleanValue);
                  }
                }}
                onBlur={() => {
                  if (curPostedPxMode === "Price") commitMonthChange();
                }}
                readOnly={curPostedPxMode !== "Price"}
              />
            </div>
          )}

          {showTruckPriceRow && (
            <div
              id={`${isKoch ? "koch-truck" : "competitor-truck"}-${
                origin.id
              }-price-row`}
              className="price-row"
            >
              <div className="price-row-icon">
                <TruckIconFO color="#60A5FA" />
              </div>
              <span className="price-row-label">
                {isKoch ? "ALT" : "Price"}
              </span>
              {renderModeButtons(curTruckMode, onTruckModeChange)}
              <input
                type="text"
                id={
                  isKoch ? "truck-price-input" : "competitor-truck-price-input"
                }
                inputMode="numeric"
                placeholder="Price"
                className={`price-display-input ${
                  curTruckMode !== "Price" ? "is-italic" : ""
                } 
            ${
              !getDisplayValue(curTruckMode, truckAltValue)
                ? "is-default-placeholder"
                : ""
            }`}
                value={getDisplayValue(curTruckMode, truckAltValue)}
                onFocus={selectPrice}
                onClick={selectPrice}
                onContextMenu={(event) => event.preventDefault()}
                onChange={(e) => {
                  if (curTruckMode === "Price") {
                    const cleanValue = e.target.value.replace(/[^0-9.]/g, "");
                    onTruckAltChange(cleanValue);
                  }
                }}
                onBlur={() => {
                  if (curTruckMode === "Price") commitMonthChange();
                }}
                readOnly={curTruckMode !== "Price"}
              />
            </div>
          )}

          {showRailPriceRow && (
            <div
              id={`${isKoch ? "koch-rail" : "competitor-rail"}-${
                origin.id
              }-price-row`}
              className="price-row"
            >
              <div className="price-row-icon">
                <RailIconFO color="#F97316" />
              </div>
              <span className="price-row-label">
                {isKoch ? "ALT" : "Price"}
              </span>
              {renderModeButtons(curRailMode, onRailModeChange)}
              <input
                id={isKoch ? "rail-price-input" : "competitor-rail-price-input"}
                type="text"
                inputMode="numeric"
                placeholder="Price"
                className={`price-display-input ${
                  curRailMode !== "Price" ? "is-italic" : ""
                } 
            ${
              !getDisplayValue(curRailMode, railAltValue)
                ? "is-default-placeholder"
                : ""
            }`}
                value={getDisplayValue(curRailMode, railAltValue)}
                onFocus={selectPrice}
                onClick={selectPrice}
                onContextMenu={(event) => event.preventDefault()}
                onChange={(e) => {
                  if (curRailMode === "Price") {
                    const cleanValue = e.target.value.replace(/[^0-9.]/g, "");
                    onRailAltChange(cleanValue);
                  }
                }}
                onBlur={() => {
                  if (curRailMode === "Price") commitMonthChange();
                }}
                readOnly={curRailMode !== "Price"}
              />
            </div>
          )}
        </div>
      );
    };

    return (
      <div id={`origin-row-${origin.id}`} className="origin-row-card">
        <div className="origin-row-header">
          {type === "koch" ? (
            <img
              data-tooltip-id="location-icon-blue-background"
              src={icons.LocationIconBlueBackground}
              alt="Location Icon Blue Background"
            />
          ) : (
            <img
              data-tooltip-id="location-icon-red-background"
              src={icons.LocationIconRedBackground}
              alt="Location Icon Red Background"
            />
          )}
          <div className="origin-name-mobile">{origin.name}</div>
          <div className="header-actions">
            <div className="action-menu-container">
              {type === "koch" ? (
                <Button
                  id="location-overview-button"
                  className="action-menu-button"
                  onClick={() => onAction("overview", origin)}
                >
                  <img
                    src={icons.InformationIconGreyBackground}
                    alt="Location Overview Mobile"
                  />
                </Button>
              ) : (
                <Button
                  id="action-button"
                  className="action-menu-button"
                  onClick={handleMenuToggle}
                >
                  <img src={icons.ThreeDotIcon} alt="Actions" />
                </Button>
              )}
              {isMenuOpen && (
                <div className="action-menu">
                  <div
                    onClick={(e) => e.stopPropagation()}
                    role="menu"
                    tabIndex={-1}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {menuItems.map((item) => (
                      <button
                        id={`mobile-${item.id}-id`}
                        key={item.id}
                        type="button"
                        onClick={() =>
                          handleActionClick(item.action as ActionType)
                        }
                        className={`dropdown-menu-item ${item.className}`}
                      >
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Button
              id="remove-button"
              className="remove-origin-button"
              onClick={handleRemove}
            >
              <img src={icons.TrashCanIcon} alt="Remove" />
            </Button>
          </div>
        </div>

        {showPriceSection && (
          <div
            id="split-origin-inputs-container"
            className="origin-inputs-container"
          >
            <div id="price-inputs-header" className="price-inputs-header">
              <div className="price-inputs-left">
                <span className="price-inputs-date-label">{dateLabel}</span>
                {type === "koch" && (
                  <button
                    type="button"
                    className={`split-unsplit-btn${isSplit ? " active" : ""}`}
                    onClick={handleSplitToggle}
                  >
                    <img
                      data-tooltip-id="split-icon"
                      src={icons.SplitIcon}
                      alt="Split Icon"
                    />
                    {isSplit ? "Unsplit" : "Split"}
                  </button>
                )}
              </div>

              <div className="last-updated-wrap" ref={lastUpdatedWrapRef}>
                <span className="last-updated-label">Last Updated By</span>
                <button
                  type="button"
                  className="last-updated-info-btn"
                  aria-label="Last Updated By Details"
                  onClick={handleLastUpdatedInfoClick}
                >
                  <InfoOutlinedIcon className="last-updated-info-icon" />
                </button>
                {isLastUpdatedPopupOpen && (
                  <div className="last-updated-popup-card">
                    <div className="last-updated-popup-header">
                      <span className="last-updated-popup-title">
                        Last Updated By
                      </span>
                      <button
                        type="button"
                        className="last-updated-popup-close-btn"
                        aria-label="Close Last Updated Tooltip"
                        onClick={() => setLastUpdatedAnchorEl(null)}
                      >
                        ×
                      </button>
                    </div>
                    {periodUpdateSections.map((section, index) => {
                      const isSplitLayout = periodUpdateSections.length > 1;

                      return (
                        <div
                          key={section.half || index}
                          className="last-updated-popup-section"
                        >
                          {section.half && (
                            <div className="last-updated-popup-half-label">
                              {section.half}
                            </div>
                          )}
                          <div className="last-updated-popup-name">
                            {section.requestedBy}
                          </div>
                          {!isSplitLayout && (
                            <div className="last-updated-popup-divider" />
                          )}
                          <div className="last-updated-popup-rows">
                            {section.rows.map((row) => (
                              <div
                                key={row.label}
                                className="last-updated-popup-row"
                              >
                                <span className="last-updated-popup-row-label">
                                  {row.label}
                                </span>
                                <span className="last-updated-popup-row-value">
                                  {formatRequestedAtDateTime(row.value)}
                                </span>
                              </div>
                            ))}
                          </div>
                          {isSplitLayout && index === 0 && (
                            <div className="last-updated-popup-divider" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {isSplit ? (
              <>
                <div className="half-section-label">1ST HALF</div>
                {renderPriceRows("first")}
                <div className="half-section-label">2ND HALF</div>
                {renderPriceRows("second")}
              </>
            ) : (
              renderPriceRows("main")
            )}
          </div>
        )}
      </div>
    );
  },
);

SplitOriginRow.displayName = "SplitOriginRow";

export default SplitOriginRow;
